/**
 * Utility functions for annotation functionality
 * Provides reusable functions for position formatting and annotation calculations
 */

import type { ModelViewerElement } from '@google/model-viewer';
import { $updateSize } from '@google/model-viewer/lib/model-viewer-base.js';
import { ANNOTATION_DEFAULTS } from './annotationConstants';
import { formatCameraOrbitString, formatFieldOfViewDeg } from './modelViewerZoom';

type ModelViewerWithUpdateSize = ModelViewerElement & {
  [$updateSize](size: { width: number; height: number }): void;
};

/** Snapshot of model-viewer camera at a moment (placement or restore). */
export interface ViewerCameraSnapshot {
  cameraTarget: string;
  cameraOrbit: string;
  cameraFieldOfView: string;
}

/**
 * Reads the current camera from model-viewer so annotations can restore the exact same view on click.
 * Orbit is relative to camera target (pivot), not the hotspot position.
 */
export function captureViewerCameraState(mv: ModelViewerElement): ViewerCameraSnapshot {
  try {
    const cameraTarget = mv.getCameraTarget().toString();
    const { theta, phi, radius } = mv.getCameraOrbit();
    const cameraOrbit = formatCameraOrbitString(
      (theta * 180) / Math.PI,
      (phi * 180) / Math.PI,
      radius
    );
    const fovDeg = mv.getFieldOfView();
    const cameraFieldOfView =
      typeof fovDeg === 'number' && Number.isFinite(fovDeg)
        ? formatFieldOfViewDeg(fovDeg)
        : ANNOTATION_DEFAULTS.FIELD_OF_VIEW;
    return { cameraTarget, cameraOrbit, cameraFieldOfView };
  } catch {
    return {
      cameraTarget: 'auto auto auto',
      cameraOrbit: ANNOTATION_DEFAULTS.MODEL_VIEWER_ORBIT,
      cameraFieldOfView: ANNOTATION_DEFAULTS.FIELD_OF_VIEW,
    };
  }
}

/**
 * Formats a 3D position vector to model-viewer format
 * @param x - X coordinate
 * @param y - Y coordinate
 * @param z - Z coordinate
 * @returns Formatted position string (e.g., "1.234m 5.678m 9.012m")
 */
export function formatPosition(x: number, y: number, z: number): string {
  return `${x.toFixed(3)}m ${y.toFixed(3)}m ${z.toFixed(3)}m`;
}

/**
 * Formats a 3D normal vector to model-viewer format
 * @param x - X component of normal vector
 * @param y - Y component of normal vector
 * @param z - Z component of normal vector
 * @returns Formatted normal string (e.g., "0 1 0")
 */
export function formatNormal(x: number, y: number, z: number): string {
  return `${x.toFixed(1)} ${y.toFixed(1)} ${z.toFixed(1)}`;
}

export type AnnotationSurfacePick = {
  position: string;
  normal: string;
};

function surfacePickFromHit(
  hit: { position: { x: number; y: number; z: number }; normal: { x: number; y: number; z: number } }
): AnnotationSurfacePick {
  return {
    position: formatPosition(hit.position.x, hit.position.y, hit.position.z),
    normal: formatNormal(hit.normal.x, hit.normal.y, hit.normal.z),
  };
}

/** Keep model-viewer internal scene size aligned with layout (fixes NDC / raycast misses). */
export function syncModelViewerLayout(mv: ModelViewerElement): void {
  const rect = mv.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) {
    return;
  }

  try {
    (mv as ModelViewerWithUpdateSize)[$updateSize]({
      width: rect.width,
      height: rect.height,
    });
  } catch {
    // Viewer not fully upgraded yet.
  }
}

function isModelViewerReady(mv: ModelViewerElement): boolean {
  try {
    if (!mv.loaded) {
      return false;
    }
    const dims = mv.getDimensions();
    return dims.x > 0 || dims.y > 0 || dims.z > 0;
  } catch {
    return false;
  }
}

async function waitForModelViewerReady(mv: ModelViewerElement): Promise<boolean> {
  const deadline = Date.now() + 8000;

  while (Date.now() < deadline) {
    syncModelViewerLayout(mv);
    if (isModelViewerReady(mv)) {
      return true;
    }

    await new Promise<void>((resolve) => {
      let settled = false;
      const done = () => {
        if (settled) return;
        settled = true;
        mv.removeEventListener('load', done);
        resolve();
      };

      mv.addEventListener('load', done);
      window.setTimeout(done, 200);
    });

    await waitAnimationFrames(1);
  }

  return isModelViewerReady(mv);
}

function waitAnimationFrames(frameCount: number): Promise<void> {
  return new Promise((resolve) => {
    let remaining = frameCount;
    const step = () => {
      remaining -= 1;
      if (remaining <= 0) {
        resolve();
        return;
      }
      requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  });
}

/**
 * Raycasts the model surface at viewport (client) coordinates.
 * model-viewer getNDC() expects clientX/clientY, not element-relative pixels.
 */
export function pickAnnotationSurfaceSync(
  mv: ModelViewerElement,
  clientX: number,
  clientY: number
): AnnotationSurfacePick | null {
  syncModelViewerLayout(mv);

  try {
    const hit = mv.positionAndNormalFromPoint(clientX, clientY);
    if (!hit) {
      return null;
    }
    return surfacePickFromHit(hit);
  } catch {
    return null;
  }
}

/**
 * Picks a model surface with short retries so the scene/camera can settle
 * (matches model-viewer test helpers and the legacy click-processing delay).
 */
export async function pickAnnotationSurfaceAtClientPoint(
  mv: ModelViewerElement,
  clientX: number,
  clientY: number
): Promise<AnnotationSurfacePick | null> {
  const ready = await waitForModelViewerReady(mv);
  if (!ready) {
    return null;
  }

  // mouseup fires after camera-controls pointerup — same timing as the legacy placement code.
  await new Promise<void>((resolve) => {
    setTimeout(resolve, 0);
  });

  let pick = pickAnnotationSurfaceSync(mv, clientX, clientY);
  if (pick) {
    return pick;
  }

  await waitAnimationFrames(2);
  pick = pickAnnotationSurfaceSync(mv, clientX, clientY);
  if (pick) {
    return pick;
  }

  await new Promise<void>((resolve) => {
    setTimeout(resolve, ANNOTATION_DEFAULTS.CLICK_PROCESSING_DELAY);
  });

  return pickAnnotationSurfaceSync(mv, clientX, clientY);
}

/**
 * Calculates a good viewing orbit for an annotation based on current camera position
 * Uses smart defaults: preserves user's angle if reasonable, otherwise uses defaults
 * @param currentOrbit - Current camera orbit object (optional)
 * @param defaultTheta - Default horizontal angle in degrees (uses constant if not provided)
 * @param defaultPhi - Default vertical angle in degrees (uses constant if not provided)
 * @param defaultRadius - Default camera distance in meters (uses constant if not provided)
 * @returns Formatted orbit string (e.g., "45deg 65deg 0.6m")
 */
export function calculateAnnotationOrbit(
  currentOrbit?: { theta: number; phi: number; radius: number } | null,
  defaultTheta: number = ANNOTATION_DEFAULTS.DEFAULT_THETA,
  defaultPhi: number = ANNOTATION_DEFAULTS.DEFAULT_PHI,
  defaultRadius: number = ANNOTATION_DEFAULTS.DEFAULT_RADIUS,
  /** Model longest edge (m); when set, min/max orbit radius scale with the loaded GLB. */
  modelMaxDimM?: number
): string {
  const minRadius =
    modelMaxDimM != null && modelMaxDimM > 0
      ? Math.max(modelMaxDimM * 0.02, 0.01)
      : ANNOTATION_DEFAULTS.MIN_RADIUS;
  const maxRadius =
    modelMaxDimM != null && modelMaxDimM > 0
      ? Math.min(modelMaxDimM * 50, 10_000)
      : ANNOTATION_DEFAULTS.MAX_RADIUS;
  // Always use current horizontal angle (theta) if available, otherwise default
  const theta = currentOrbit ? Math.round(currentOrbit.theta * (180 / Math.PI)) : defaultTheta;
  
  // Smart default for phi (vertical angle): use user's angle if reasonable, otherwise default
  let phi: number;
  if (currentOrbit) {
    const phiRadians = currentOrbit.phi;
    // Check if phi is within reasonable range (not too low or too high)
    if (phiRadians >= ANNOTATION_DEFAULTS.MIN_PHI_RADIANS && phiRadians <= ANNOTATION_DEFAULTS.MAX_PHI_RADIANS) {
      // User's angle is reasonable, use it
      phi = Math.round(phiRadians * (180 / Math.PI));
    } else {
      // Angle is too extreme, use default
      phi = defaultPhi;
    }
  } else {
    phi = defaultPhi;
  }
  
  // Smart default for radius: preserve user's zoom level as much as possible
  let radius: number;
  if (currentOrbit) {
    const currentRadius = currentOrbit.radius;
    // Always use user's radius if it's above absolute minimum
    // Only clamp if it's below absolute minimum (0.05m) to prevent unusable tiny points
    if (currentRadius >= minRadius) {
      if (currentRadius <= maxRadius) {
        // User's distance is within reasonable range, use it exactly (rounded to 2 decimals)
        radius = Math.round(currentRadius * 100) / 100;
      } else {
        // Distance is too far, use default
        radius = defaultRadius;
      }
    } else {
      // User zoomed extremely close (below 0.05m) - clamp to minimum to prevent unusable tiny point
      radius = minRadius;
    }
  } else {
    radius = defaultRadius;
  }
  
  return `${theta}deg ${phi}deg ${radius}m`;
}

/**
 * Generates the next annotation label based on current annotations count
 * @param annotationsCount - Current number of annotations
 * @returns Next label as string (e.g., "1", "2", "3")
 */
export function generateNextAnnotationLabel(annotationsCount: number): string {
  return String(annotationsCount + 1);
}

/**
 * Creates fallback annotation data when hit result is not available
 * Uses current camera target as position
 * @param currentTarget - Current camera target object with x, y, z coordinates
 * @param currentOrbit - Optional current camera orbit to preserve user's viewing angle
 * @returns Object containing position, normal, orbit, and target strings
 */
export function createFallbackAnnotationData(
  currentTarget: { x: number; y: number; z: number },
  currentOrbit?: { theta: number; phi: number; radius: number } | null
) {
  const position = formatPosition(currentTarget.x, currentTarget.y, currentTarget.z);
  const normal = ANNOTATION_DEFAULTS.NORMAL;
  // Use smart default orbit calculation if currentOrbit is provided
  const orbit = currentOrbit 
    ? calculateAnnotationOrbit(currentOrbit)
    : ANNOTATION_DEFAULTS.MODEL_VIEWER_ORBIT;
  const target = position; // Target should be the annotation position itself
  
  return {
    position,
    normal,
    orbit,
    target,
  };
}

/**
 * Checks if a URL is a YouTube video URL (youtube.com or youtu.be)
 */
export function isYouTubeUrl(url: string): boolean {
  if (!url || typeof url !== 'string') return false;
  try {
    const u = new URL(url.trim());
    return (
      u.hostname === 'www.youtube.com' ||
      u.hostname === 'youtube.com' ||
      u.hostname === 'm.youtube.com' ||
      u.hostname === 'youtu.be'
    );
  } catch {
    return false;
  }
}

/** Only http(s) links are safe for annotation href / embeds. */
export function isSafeHttpUrl(url: string): boolean {
  if (!url || typeof url !== 'string') return false;
  try {
    const u = new URL(url.trim());
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Turn typed annotation links into http(s) URLs.
 * "www.example.com" / "example.com/path" → https://… ; javascript:/data: stay rejected.
 */
export function normalizeAnnotationHttpUrl(raw: string): string | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  if (isSafeHttpUrl(trimmed)) return trimmed;

  const lower = trimmed.toLowerCase();
  if (
    lower.startsWith('javascript:') ||
    lower.startsWith('data:') ||
    lower.startsWith('vbscript:') ||
    lower.startsWith('file:') ||
    trimmed.includes('://')
  ) {
    return undefined;
  }

  const candidate = `https://${trimmed}`;
  return isSafeHttpUrl(candidate) ? candidate : undefined;
}

/** Raster data URLs or http(s) — blocks SVG/HTML data URLs in annotation images. */
export function isSafeAnnotationImageSrc(src: string): boolean {
  if (!src || typeof src !== 'string') return false;
  const trimmed = src.trim();
  if (isSafeHttpUrl(trimmed)) return true;
  return /^data:image\/(png|jpeg|jpg|webp|gif);base64,/i.test(trimmed);
}

/**
 * Extracts YouTube video ID from various YouTube URL formats.
 * Returns null if not a valid YouTube URL or ID cannot be extracted.
 */
export function getYouTubeVideoId(url: string): string | null {
  if (!url || typeof url !== 'string') return null;
  try {
    const u = new URL(url.trim());
    if (u.hostname === 'youtu.be') {
      return u.pathname.slice(1).split('?')[0] || null;
    }
    if (
      u.hostname === 'www.youtube.com' ||
      u.hostname === 'youtube.com' ||
      u.hostname === 'm.youtube.com'
    ) {
      if (u.pathname === '/watch') return u.searchParams.get('v');
      const embedMatch = u.pathname.match(/^\/embed\/([^/?]+)/);
      if (embedMatch) return embedMatch[1];
      const shortMatch = u.pathname.match(/^\/v\/([^/?]+)/);
      if (shortMatch) return shortMatch[1];
    }
    return null;
  } catch {
    return null;
  }
}
